import { cn } from "@patient-watch/ui/utils";
import { CheckCircle, Circle, CircleDashed, CircleDot } from "lucide-react";

export type StageT = "future" | "current" | "past" | "skipped";

export interface StepT {
    key?: string;
    stage: StageT;
    words: string;
    accessible?: boolean;
    status?: string;
    canNavigate?: boolean;
    route?: string;
}

interface Props {
    steps: StepT[];
    onStepChange?: (stepIndex: number) => void;
}

export const stageColors = (stage: StageT) => {
    return cn(
        stage === "current" && "border-fuchsia-600 text-fuchsia-600 dark:border-fuchsia-400 dark:text-fuchsia-400",
        stage === "future" && "border-slate-400 text-slate-400 dark:border-slate-700 dark:text-slate-500",
        stage === "past" && "border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400",
        stage === "skipped" && "border-slate-600 text-slate-600 dark:border-slate-400 dark:text-slate-400 border-dashed"
    );
};

const Stepper = ({ steps, onStepChange }: Props) => {
    return (
        <ul className="flex-col flex-wrap justify-center items-center space-y-3 mx-auto w-full">
            {steps?.map((step, index) => (
                <li className="flex flex-col w-full" key={step.words}>
                    <div className="flex justify-center items-center w-full">
                        <div
                            className={cn(
                                "flex-1 transition-all",
                                step.canNavigate || step.stage === "past" || step.stage === "current"
                                    ? "cursor-pointer"
                                    : "cursor-default opacity-60"
                            )}
                            onClick={() => {
                                if (
                                    (step.canNavigate || step.stage === "past" || step.stage === "current") &&
                                    onStepChange
                                ) {
                                    onStepChange(index);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (
                                    (e.key === "Enter" || e.key === " ") &&
                                    (step.canNavigate || step.stage === "past" || step.stage === "current") &&
                                    onStepChange
                                ) {
                                    e.preventDefault();
                                    onStepChange(index);
                                }
                            }}
                            role="button"
                            tabIndex={step.canNavigate || step.stage === "past" || step.stage === "current" ? 0 : -1}
                            aria-label={`${step.words} - ${step.stage}`}
                        >
                            <Step
                                stage={step.stage}
                                words={step.words}
                                accessible={step.accessible}
                                status={step.status}
                            />
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
};

export default Stepper;

interface StepProps {
    stage: StageT;
    words: string;
    accessible?: boolean;
    status?: string;
}
const Step = (props: StepProps) => {
    return (
        <div
            className={cn(
                stageColors(props.stage),
                "w-full text-sm flex flex-nowrap items-center justify-between gap-x-4"
            )}
        >
            <div className="flex justify-center items-center gap-x-2">
                <span className={cn("hover:underline underline-offset-2", stageColors(props.stage))}>
                    {props.stage === "future" && <Circle size={20} />}
                    {props.stage === "skipped" && <CircleDashed size={20} />}
                    {props.stage === "past" && <CheckCircle size={20} />}
                    {props.stage === "current" && <CircleDot size={20} />}
                </span>
                <div className="flex flex-col">
                    <p
                        className={cn(
                            stageColors(props.stage),
                            "text-center font-medium hover:underline underline-offset-2"
                        )}
                    >
                        {props.words}
                    </p>
                </div>
            </div>
            {props.status && (
                <p className={cn("opacity-90 mt-1 text-xs text-center", stageColors(props.stage))}>{props.status}</p>
            )}
        </div>
    );
};
